/**
 * Global currency manager singleton
 * Ensures exchange rate is fetched only once across entire application
 */
import { Currency, formatCurrency, getCurrencyPreference, setCurrencyPreference } from './currency';
import { getCache, saveCache } from './storage';
import { debugLog } from './debug-log';

interface ExchangeRateResponse {
  result: string;
  provider: string;
  documentation: string;
  terms_of_use: string;
  time_last_update_unix: number;
  time_last_update_utc: string;
  time_next_update_unix: number;
  time_next_update_utc: string;
  time_eol_unix: number;
  base_code: string;
  rates: {
    [currency: string]: number;
  };
}

interface ExchangeRateCachePayload {
  rate: number;
  fetchedAt: number;
  isFallback: boolean;
}

class CurrencyManager {
  private static instance: CurrencyManager | null = null;
  private exchangeRate: number | null = null;
  private isLoading: boolean = false;
  private hasInitialized: boolean = false;
  private currency: Currency;
  private listeners: Set<() => void> = new Set();
  private exchangeRateTimestamp: number | null = null;
  private isFallbackRateFlag: boolean = false;

  private constructor() {
    this.currency = getCurrencyPreference();
  }

  public static getInstance(): CurrencyManager {
    if (!CurrencyManager.instance) {
      CurrencyManager.instance = new CurrencyManager();
    }
    return CurrencyManager.instance;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  public async initialize(): Promise<void> {
    if (this.hasInitialized) {
      return;
    }

    this.hasInitialized = true;
    await this.loadExchangeRate();
  }

  private async loadExchangeRate(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.notifyListeners();

    try {
      // Check cache first (new format)
      const cachedRatePayload = getCache<ExchangeRateCachePayload>('exchange_rates_v2');
      if (cachedRatePayload) {
        debugLog('Using cached exchange rate (v2):', cachedRatePayload.rate);
        this.exchangeRate = cachedRatePayload.rate;
        this.exchangeRateTimestamp = cachedRatePayload.fetchedAt;
        this.isFallbackRateFlag = !!cachedRatePayload.isFallback;
        this.isLoading = false;
        this.notifyListeners();
        return;
      }

      // Legacy cache fallback
      const cachedRate = getCache<number>('exchange_rates');
      if (cachedRate) {
        debugLog('Using cached exchange rate (legacy):', cachedRate);
        this.exchangeRate = cachedRate;
        this.exchangeRateTimestamp = Date.now();
        this.isFallbackRateFlag = false;
        saveCache('exchange_rates_v2', {
          rate: cachedRate,
          fetchedAt: this.exchangeRateTimestamp,
          isFallback: false
        }, 24 * 60 * 60 * 1000);
        this.isLoading = false;
        this.notifyListeners();
        return;
      }

      // Check if we recently failed
      const recentFailure = getCache<boolean>('exchange_rate_failed');
      if (recentFailure) {
        debugLog('Recently failed to fetch exchange rate, using fallback');
        this.exchangeRate = 145.0;
        this.exchangeRateTimestamp = Date.now();
        this.isFallbackRateFlag = true;
        this.isLoading = false;
        saveCache('exchange_rates_v2', {
          rate: this.exchangeRate,
          fetchedAt: this.exchangeRateTimestamp,
          isFallback: true
        }, 24 * 60 * 60 * 1000);
        this.notifyListeners();
        return;
      }

      // Fetch from API (only once)
      debugLog('Fetching exchange rate from API...');
      const url = 'https://open.er-api.com/v6/latest/USD';

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: AbortSignal.timeout(5000)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ExchangeRateResponse = await response.json();
      
      if (data.result !== 'success') {
        throw new Error('API returned error result');
      }

      const rate = data.rates.JPY;
      if (!rate) {
        throw new Error('JPY rate not found in response');
      }

      // Cache the rate for 24 hours
      this.exchangeRateTimestamp = data.time_last_update_unix
        ? data.time_last_update_unix * 1000
        : Date.now();
      this.isFallbackRateFlag = false;
      saveCache('exchange_rates', rate, 24 * 60 * 60 * 1000);
      saveCache('exchange_rates_v2', {
        rate,
        fetchedAt: this.exchangeRateTimestamp,
        isFallback: false
      }, 24 * 60 * 60 * 1000);
      
      this.exchangeRate = rate;
      debugLog('Fetched new exchange rate:', rate, 'at', data.time_last_update_utc);

    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      
      // Cache the failure for 24 hours
      saveCache('exchange_rate_failed', true, 24 * 60 * 60 * 1000);
      
      // Use fallback rate
      this.exchangeRate = 145.0;
      this.exchangeRateTimestamp = Date.now();
      this.isFallbackRateFlag = true;
      console.warn('Using fallback exchange rate:', this.exchangeRate);
      
      // Cache the fallback rate
      saveCache('exchange_rates', this.exchangeRate, 24 * 60 * 60 * 1000);
      saveCache('exchange_rates_v2', {
        rate: this.exchangeRate,
        fetchedAt: this.exchangeRateTimestamp,
        isFallback: true
      }, 24 * 60 * 60 * 1000);
    } finally {
      this.isLoading = false;
      this.notifyListeners();
    }
  }

  public getExchangeRate(): number | null {
    return this.exchangeRate;
  }

  public getExchangeRateTimestamp(): number | null {
    return this.exchangeRateTimestamp;
  }

  public isLoadingRate(): boolean {
    return this.isLoading;
  }

  public isFallbackRate(): boolean {
    return this.isFallbackRateFlag;
  }

  public getCurrency(): Currency {
    return this.currency;
  }

  public toggleCurrency(): void {
    this.currency = this.currency === 'USD' ? 'JPY' : 'USD';
    setCurrencyPreference(this.currency);
    this.notifyListeners();
  }

  public formatAmountSync(amount: number, sourceCurrency: Currency = 'USD'): string {
    if (sourceCurrency === this.currency) {
      return formatCurrency(amount, this.currency);
    }

    if (sourceCurrency === 'USD' && this.currency === 'JPY' && this.exchangeRate) {
      const convertedAmount = amount * this.exchangeRate;
      return formatCurrency(convertedAmount, this.currency);
    }

    if (sourceCurrency === 'JPY' && this.currency === 'USD' && this.exchangeRate) {
      const convertedAmount = amount / this.exchangeRate;
      return formatCurrency(convertedAmount, this.currency);
    }

    // Fallback to original currency if no rate available
    return formatCurrency(amount, sourceCurrency);
  }
}

export default CurrencyManager;
